/**
 * Forward-only payment status transitions (admin UI; mirrors order-status UX).
 * Align with backend Payment.STATUS_CHOICES.
 */
export type PaymentStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED';

const ALL: PaymentStatus[] = [
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
];

export function isPaymentStatus(s: string): s is PaymentStatus {
  return ALL.includes(s as PaymentStatus);
}

/** Next statuses an admin may set (no going backward on the main success path). */
export function getAllowedNextPaymentStatuses(current: string): PaymentStatus[] {
  if (!isPaymentStatus(current)) {
    return [];
  }
  switch (current) {
    case 'PENDING':
      return ['PROCESSING', 'COMPLETED', 'FAILED'];
    case 'PROCESSING':
      return ['COMPLETED', 'FAILED'];
    case 'FAILED':
      return ['PROCESSING', 'COMPLETED'];
    case 'COMPLETED':
      return ['REFUNDED', 'PARTIALLY_REFUNDED'];
    case 'PARTIALLY_REFUNDED':
      return ['REFUNDED'];
    case 'REFUNDED':
    default:
      return [];
  }
}

export function splitPaymentStatusesForMenu(allowed: PaymentStatus[]): {
  progress: PaymentStatus[];
  markFailed: PaymentStatus[];
  refunds: PaymentStatus[];
} {
  const progress: PaymentStatus[] = [];
  const markFailed: PaymentStatus[] = [];
  const refunds: PaymentStatus[] = [];
  for (const s of allowed) {
    if (s === 'FAILED') {
      markFailed.push(s);
    } else if (s === 'REFUNDED' || s === 'PARTIALLY_REFUNDED') {
      refunds.push(s);
    } else {
      progress.push(s);
    }
  }
  return { progress, markFailed, refunds };
}

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  PENDING: 'Pending',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
  PARTIALLY_REFUNDED: 'Partially Refunded',
};

export function formatPaymentStatusUpdateError(err: unknown): string {
  const e = err as { data?: { message?: string; errors?: Record<string, string | string[]> } };
  const statusErr = e?.data?.errors?.status;
  if (statusErr !== undefined) {
    return Array.isArray(statusErr) ? String(statusErr[0]) : String(statusErr);
  }
  const detail = e?.data?.errors?.detail;
  if (detail !== undefined) {
    return Array.isArray(detail) ? String(detail[0]) : String(detail);
  }
  if (e?.data?.message) {
    return String(e.data.message);
  }
  return 'Failed to update payment status';
}
